import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const Services = () => {
  const observerRef = useRef();

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observerRef.current.observe(el));

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-white">
        <div className="container-max">
          <div className="text-center max-w-4xl mx-auto animate-slide-up">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="gradient-text">Comprehensive</span> Testing Solutions
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              From API validation to complete application security - we're building the most advanced 
              testing automation platform in the industry.
            </p>
            <div className="flex justify-center space-x-6 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-accent-500 rounded-full"></div>
                <span>Available Now</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span>Coming Soon</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Current Service - API Testing */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center animate-on-scroll">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-4 h-4 bg-accent-500 rounded-full"></div>
                <span className="text-accent-600 font-semibold text-sm uppercase tracking-wide">Available Now</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Intelligent API Testing
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                While other tools require extensive manual configuration and coding expertise, 
                our platform generates comprehensive test suites automatically. No more writing hundreds 
                of test cases by hand or dealing with complex framework setup.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mt-1">
                    <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Automatic Test Generation</h3>
                    <p className="text-gray-600">Import your OpenAPI/Swagger specs and get complete REST Assured test suites instantly.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mt-1">
                    <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Smart Validation Rules</h3>
                    <p className="text-gray-600">Advanced validation logic that goes beyond basic response checking to ensure data integrity.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mt-1">
                    <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">CI/CD Ready</h3>
                    <p className="text-gray-600">Seamless integration with Jenkins, GitHub Actions, GitLab CI, and other popular pipelines.</p>
                  </div>
                </div>
              </div>

              <a
                href="https://ejbed.com/app/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center space-x-2"
              >
                <span>Try API Testing Now</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1551650975-87deedd944c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                alt="API Testing Interface" 
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-xl">
                <div className="text-2xl font-bold text-primary-600">99.9%</div>
                <div className="text-sm text-gray-600">Test Coverage</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              EJBED vs Traditional Testing Tools
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See why development teams are switching from legacy testing frameworks to our modern automation platform.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden animate-on-scroll">
            <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">
              <div className="p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Feature</h3>
                <div className="space-y-4 text-gray-600">
                  <div>Setup Time</div>
                  <div>Test Creation</div>
                  <div>Maintenance</div>
                  <div>Learning Curve</div>
                  <div>Framework Dependencies</div>
                  <div>Code Generation</div>
                  <div>Real-time Reporting</div>
                </div>
              </div>

              <div className="p-8 bg-red-50">
                <h3 className="text-xl font-semibold text-red-700 mb-4">Traditional Tools</h3>
                <div className="space-y-4 text-gray-600">
                  <div className="flex items-center"><span className="text-red-500 mr-2">❌</span> 2-4 weeks</div>
                  <div className="flex items-center"><span className="text-red-500 mr-2">❌</span> Manual coding required</div>
                  <div className="flex items-center"><span className="text-red-500 mr-2">❌</span> High maintenance overhead</div>
                  <div className="flex items-center"><span className="text-red-500 mr-2">❌</span> Steep learning curve</div>
                  <div className="flex items-center"><span className="text-red-500 mr-2">❌</span> Complex dependencies</div>
                  <div className="flex items-center"><span className="text-red-500 mr-2">❌</span> Limited automation</div>
                  <div className="flex items-center"><span className="text-red-500 mr-2">❌</span> Basic reporting</div>
                </div>
              </div>

              <div className="p-8 bg-accent-50">
                <h3 className="text-xl font-semibold text-accent-700 mb-4">EJBED Platform</h3>
                <div className="space-y-4 text-gray-600">
                  <div className="flex items-center"><span className="text-accent-500 mr-2">✅</span> 5 minutes</div>
                  <div className="flex items-center"><span className="text-accent-500 mr-2">✅</span> Fully automated</div>
                  <div className="flex items-center"><span className="text-accent-500 mr-2">✅</span> Self-maintaining</div>
                  <div className="flex items-center"><span className="text-accent-500 mr-2">✅</span> Zero learning curve</div>
                  <div className="flex items-center"><span className="text-accent-500 mr-2">✅</span> Zero dependencies</div>
                  <div className="flex items-center"><span className="text-accent-500 mr-2">✅</span> Intelligent generation</div>
                  <div className="flex items-center"><span className="text-accent-500 mr-2">✅</span> Advanced analytics</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Future Services */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Next-Generation Testing <span className="gradient-text">Coming Soon</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're not stopping at API testing. Our roadmap includes the most comprehensive testing automation suite ever built.
            </p>
          </div>

          <div className="space-y-16 animate-on-scroll">
            {/* UI Testing */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <img 
                  src="https://images.unsplash.com/photo-1551650975-87deedd944c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="UI Testing Automation" 
                  className="rounded-lg shadow-2xl"
                />
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                  <span className="text-orange-600 font-semibold text-sm uppercase tracking-wide">Q4 2025</span>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Advanced UI Testing Automation
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  While most UI testing tools struggle with modern frameworks and dynamic content, 
                  our AI-powered approach adapts to any interface automatically.
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Intelligent element detection that works with any framework</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Cross-browser testing across 50+ browser/OS combinations</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Visual regression testing with pixel-perfect comparison</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Performance Testing */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                  <span className="text-orange-600 font-semibold text-sm uppercase tracking-wide">Q1 2026</span>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Enterprise Performance Testing
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Most performance testing tools require expensive licenses and complex setup. 
                  Our cloud-native approach scales automatically and provides insights traditional tools miss.
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Load testing up to 1M+ concurrent users</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Real-time bottleneck identification and recommendations</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Predictive scaling analysis and cost optimization</span>
                  </li>
                </ul>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Performance Testing Dashboard" 
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>

            {/* Security Testing */}
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <img 
                  src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" 
                  alt="Security Testing" 
                  className="rounded-lg shadow-2xl"
                />
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-4 h-4 bg-orange-500 rounded-full"></div>
                  <span className="text-orange-600 font-semibold text-sm uppercase tracking-wide">Q2 2026</span>
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Comprehensive Security Testing
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Security testing shouldn't require penetration testing expertise. 
                  Our automated security suite identifies vulnerabilities that manual reviews often miss.
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>OWASP Top 10 automated vulnerability scanning</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Compliance reporting for SOC2, PCI DSS, and GDPR</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Automated penetration testing and remediation guidance</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-primary-900 text-white">
        <div className="container-max text-center animate-on-scroll">
          <h2 className="text-4xl font-bold mb-6">Ready to Experience Next-Level Testing?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Start with our powerful API testing platform today, and get early access to upcoming features.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="https://ejbed.com/app/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Start API Testing Now
            </a>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              Request Beta Access
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services; 